import React from "react"
import utilStyles from "../../asset/styles/util/util.module.scss"
import Grid from "@mui/material/Grid"
import InputText from "component/inputText/inputText"
import Select from "component/select/select"
import Toggle from "component/toggle/Toggle"
import IconsColor from "util/iconsColor"
import ExcursionPlaceDropdown, {
    ExcursionPlaceOption,
} from "component/excursionPlaceDropdown/excursionPlaceDropdown"
import styles from "../../asset/styles/dayTripDetail/dayTripDetail.module.scss"
import { DayTripDetailModel } from "./dayTripDetailTypes"
import { ExcursionData, PlaceData, SelectOption } from "../../models"
import { toTitleCase } from "util/textcase"

const COMPOSITE_PREFIX = { excursion: "excursion:", place: "place:" } as const

const isHaLongBayCity = (cityName: string): boolean =>
    cityName.trim().toLowerCase() === "ha long bay"

const filterExcursionsByCity = (excursions: ExcursionData[], cityName: string): ExcursionData[] => {
    if (!cityName.trim()) return excursions
    return excursions.filter((excursion) => excursion.city_name === cityName)
}

const clearStartEndExcursionSelections = (
    data: DayTripDetailModel,
): Partial<DayTripDetailModel> => {
    const updates: Partial<DayTripDetailModel> = {}

    if (data.start_point_id?.startsWith("excursion:")) {
        updates.start_point_id = ""
        updates.start_point_name = ""
    }
    if (data.end_point_id?.startsWith("excursion:")) {
        updates.end_point_id = ""
        updates.end_point_name = ""
    }

    return updates
}

const clearAllExcursionSelections = (
    data: DayTripDetailModel,
): Partial<DayTripDetailModel> => {
    const updates = clearStartEndExcursionSelections(data)

    if ((data.stops ?? []).some((stop) => stop.stop_id?.startsWith("excursion:"))) {
        updates.stops = (data.stops ?? []).map((stop) =>
            stop.stop_id?.startsWith("excursion:") ? { stop_id: "", stop_name: "" } : stop,
        )
    }

    return updates
}

function toCompositeId(opt: ExcursionPlaceOption): string {
    return `${COMPOSITE_PREFIX[opt.type]}${opt.id}`
}

function fromCompositeId(
    id: string,
    name: string
): ExcursionPlaceOption | null {
    if (!id || !name) return null
    if (id.startsWith("place:"))
        return { type: "place", id: id.slice(6), name, city_name: "" }
    if (id.startsWith("excursion:"))
        return { type: "excursion", id: id.slice(10), name, city_name: "" }
    return null
}

/** Resolve the city_name of a selected start/end/stop point by looking up its raw id
 *  in the excursions/places lists. Returns "" when it can't be resolved. */
function matchPointCity(
    opt: ExcursionPlaceOption | null,
    excursions: ExcursionData[],
    places: PlaceData[]
): string {
    if (!opt) return ""
    if (opt.type === "excursion") {
        return excursions.find((e) => e.excursion_id === opt.id)?.city_name ?? ""
    }
    return places.find((p) => p.place_id === opt.id)?.city_name ?? ""
}

interface DayTripConfigurationProps {
    data: DayTripDetailModel
    excursions: ExcursionData[]
    places: PlaceData[]
    tripTypeOptions: SelectOption[]
    cityOptions: SelectOption[]
    onChange: (updates: Partial<DayTripDetailModel>) => void
    errors?: { trip_name?: string; start_point?: string; end_point?: string; stops?: string; city?: string }
}

const DayTripConfiguration: React.FC<DayTripConfigurationProps> = ({
    data,
    excursions,
    places,
    tripTypeOptions,
    cityOptions,
    onChange,
    errors = {},
}) => {
    const showOnlyPlaces = data.day_trip_type_id === 1
    const lockedCityName = data.city_name || ""
    const isHaLongBay = isHaLongBayCity(lockedCityName)
    const startEndPlacesOnly = showOnlyPlaces || isHaLongBay
    const startEndExcursions: ExcursionData[] = []
    const stopExcursions = showOnlyPlaces
        ? []
        : isHaLongBay
          ? filterExcursionsByCity(excursions, lockedCityName)
          : excursions

    // When the city changes, clear any start/end/stop selection that belongs to a different city.
    const handleCityChange = (cityValue: string) => {
        const selected = cityOptions.find((opt) => String(opt.value) === cityValue)
        const nextCityName = selected?.label ?? ""
        const updates: Partial<DayTripDetailModel> = {
            city_id: cityValue ? Number(cityValue) : null,
            city_name: nextCityName,
        }

        if (!isHaLongBayCity(nextCityName)) {
            // Clear start point if it no longer matches the new city.
            const startOpt = fromCompositeId(data.start_point_id, data.start_point_name)
            const startCity = matchPointCity(startOpt, excursions, places)
            if (data.start_point_id && startCity && startCity !== nextCityName) {
                updates.start_point_id = ""
                updates.start_point_name = ""
            }
            // Clear end point if it no longer matches the new city.
            const endOpt = fromCompositeId(data.end_point_id, data.end_point_name)
            const endCity = matchPointCity(endOpt, excursions, places)
            if (data.end_point_id && endCity && endCity !== nextCityName) {
                updates.end_point_id = ""
                updates.end_point_name = ""
            }
            // Clear any stop that no longer matches the new city.
            if ((data.stops ?? []).length > 0) {
                updates.stops = (data.stops ?? []).map((stop) => {
                    const stopOpt = fromCompositeId(stop.stop_id, stop.stop_name)
                    const stopCity = matchPointCity(stopOpt, excursions, places)
                    if (stop.stop_id && stopCity && stopCity !== nextCityName) {
                        return { ...stop, stop_id: "", stop_name: "" }
                    }
                    return stop
                })
            }
        } else {
            Object.assign(updates, clearStartEndExcursionSelections(data))
            if ((data.stops ?? []).length > 0) {
                updates.stops = (data.stops ?? []).map((stop) => {
                    if (!stop.stop_id?.startsWith("excursion:")) return stop
                    const stopOpt = fromCompositeId(stop.stop_id, stop.stop_name)
                    const stopCity = matchPointCity(stopOpt, excursions, places)
                    if (stopCity && stopCity !== nextCityName) {
                        return { ...stop, stop_id: "", stop_name: "" }
                    }
                    return stop
                })
            }
        }

        onChange(updates)
    }

    const handleStartPointChange = (opt: ExcursionPlaceOption | null) => {
        if (!opt) {
            onChange({ start_point_id: "", start_point_name: "" })
            return
        }
        onChange({
            start_point_id: toCompositeId(opt),
            start_point_name: toTitleCase(opt.name),
        })
    }

    const handleEndPointChange = (opt: ExcursionPlaceOption | null) => {
        if (!opt) {
            onChange({ end_point_id: "", end_point_name: "" })
            return
        }
        onChange({
            end_point_id: toCompositeId(opt),
            end_point_name: toTitleCase(opt.name),
        })
    }

    const handleAddStop = () => {
        const stops = [...(data.stops ?? []), { stop_id: "", stop_name: "" }]
        onChange({ stops })
    }

    const handleStopChange = (index: number, opt: ExcursionPlaceOption | null) => {
        const stops = [...(data.stops ?? [])]
        if (!stops[index]) return
        if (!opt) {
            stops[index] = { stop_id: "", stop_name: "" }
        } else {
            stops[index] = { stop_id: toCompositeId(opt), stop_name: toTitleCase(opt.name) }
        }
        onChange({ stops })
    }

    const handleRemoveStop = (index: number) => {
        const stops = (data.stops ?? []).filter((_, i) => i !== index)
        onChange({ stops })
    }

    const stops = data.stops ?? []
    const placePickerProps = isHaLongBay ? { showAllPlaces: true as const } : { lockedCityName }
    const stopPickerProps = isHaLongBay
        ? { showAllPlaces: true as const, lockedExcursionsCityName: lockedCityName }
        : { lockedCityName }

    return (
        <div className={utilStyles.pageContainerContent}>
            <div className={styles.sectionHeader}>
                <h2 className={styles.sectionTitle}>Trip Configuration</h2>
                <div className={styles.statusField}>
                    <div className={styles.statusToggle}>
                        <Toggle
                            leftLabel="Inactive"
                            rightLabel="Active"
                            checked={data.flag_active}
                            onChange={(checked) => onChange({ flag_active: checked })}
                        />
                    </div>
                </div>
            </div>
            <Grid container spacing={2}>
                <Grid size={{ xs: 12, md: 12 }}>
                    <InputText
                        label="Trip Name"
                        value={toTitleCase(data.trip_name)}
                        onChange={(e) => onChange({ trip_name: toTitleCase(e.target.value) })}
                        placeholder="Enter Name"
                        required
                        error={errors.trip_name}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Select
                        label="Trip Type"
                        value={String(data.day_trip_type_id ?? "")}
                        onChange={(e) => {
                            const nextTripTypeId = e.target.value
                                ? Number(e.target.value)
                                : null
                            const selected = tripTypeOptions.find(
                                (opt) => String(opt.value) === e.target.value
                            )
                            const updates: Partial<DayTripDetailModel> = {
                                day_trip_type_id: nextTripTypeId,
                                day_trip_type_name: selected?.label ?? "",
                            }

                            if (nextTripTypeId === 1) {
                                Object.assign(updates, clearAllExcursionSelections(data))
                            }

                            onChange(updates)
                        }}
                        options={tripTypeOptions}
                        placeholder="Select trip type"
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <Select
                        label="City"
                        value={data.city_id != null ? String(data.city_id) : ""}
                        onChange={(e) => handleCityChange(e.target.value)}
                        options={cityOptions}
                        placeholder="Select city"
                        required
                        error={errors.city}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <ExcursionPlaceDropdown
                        excursions={startEndExcursions}
                        places={places}
                        label="Start Point"
                        placeholder="Select start point"
                        value={fromCompositeId(
                            data.start_point_id,
                            toTitleCase(data.start_point_name)
                        )}
                        onChange={handleStartPointChange}
                        required
                        error={errors.start_point}
                        {...placePickerProps}
                        showExcursions={!startEndPlacesOnly}
                    />
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    {/* {errors.stops && (
                        <div style={{ marginTop: 4, color: "#d32f2f", fontSize: "0.75rem" }}>
                            {errors.stops}
                        </div>
                    )} */}
                    {stops.length > 0 && (
                        <div className={styles.stopsList}>
                            {stops.map((stop, index) => (
                                <div key={index} className={styles.stopRow}>
                                    <ExcursionPlaceDropdown
                                        excursions={stopExcursions}
                                        places={places}
                                        placeholder="Select stop"
                                        value={fromCompositeId(
                                            stop.stop_id,
                                            toTitleCase(stop.stop_name)
                                        )}
                                        onChange={(opt) =>
                                            handleStopChange(index, opt)
                                        }
                                        error={errors.stops}
                                        {...stopPickerProps}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => handleRemoveStop(index)}
                                        style={{
                                            background: "none",
                                            border: "none",
                                            cursor: "pointer",
                                            padding: 0,
                                            display: "inline-flex",
                                            color: "#d32f2f",
                                        }}
                                        aria-label="Remove stop"
                                    >
                                        <IconsColor
                                            iconName="deleteIcon"
                                            width="30px"
                                            height="30px"
                                            fillColor="none"
                                            strokeColor="currentColor"
                                        />
                                    </button>
                                </div>
                            ))}
                        </div>
                    )}
                    <button
                        type="button"
                        className={styles.addStopLink}
                        onClick={handleAddStop}
                    >
                        + Add Stop
                    </button>
                </Grid>
                <Grid size={{ xs: 12, md: 12 }}>
                    <ExcursionPlaceDropdown
                        excursions={startEndExcursions}
                        places={places}
                        label="End Point"
                        placeholder="Select end point"
                        value={fromCompositeId(
                            data.end_point_id,
                            toTitleCase(data.end_point_name)
                        )}
                        onChange={handleEndPointChange}
                        required
                        error={errors.end_point}
                        {...placePickerProps}
                        showExcursions={!startEndPlacesOnly}
                    />
                </Grid>
            </Grid>
        </div>
    )
}

export default DayTripConfiguration
